import { useEffect, useState } from 'react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Card, CardHeader, CardBody } from '@/components/ui/Card'
import { KpiCard } from '@/components/ui/KpiCard'
import { Badge, StatusBadge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { toast } from '@/components/ui/Toast'
import { Stethoscope, FlaskConical, MapPin, Activity, FileSpreadsheet } from 'lucide-react'
import { CredentialingProvidersService } from '@/services/modules/CredentialingService'
import { exportSheetsToXlsx, type ExportColumn } from '@/lib/exportXlsx'

/** Linha comum às abas do export (cada ranking usa as colunas que fazem sentido). */
type RankingExportRow = { position: number; name: string; state?: string; count: number }

const NAME_RANK_COLUMNS = (nameHeader: string): ExportColumn<RankingExportRow>[] => [
  { header: '#',            accessor: (r) => r.position },
  { header: nameHeader,     accessor: (r) => r.name },
  { header: 'Credenciados', accessor: (r) => r.count },
]

const CITY_RANK_COLUMNS: ExportColumn<RankingExportRow>[] = [
  { header: '#',            accessor: (r) => r.position },
  { header: 'Cidade',       accessor: (r) => r.name },
  { header: 'UF',           accessor: (r) => r.state ?? '' },
  { header: 'Credenciados', accessor: (r) => r.count },
]

export function CredentialingDashboardPage() {
  const [statuses, setStatuses] = useState<{ status: string; count: number }[]>([])
  const [st,       setSt]       = useState<{ specialties: { id: string; name: string; providers: number }[]; tests: { id: string; name: string; providers: number }[] }>({ specialties: [], tests: [] })
  const [cities,   setCities]   = useState<{ cityId: string; cityName: string; state: string; count: number }[]>([])
  const [loading,  setLoading]  = useState(true)

  useEffect(() => {
    Promise.all([
      CredentialingProvidersService.dashboardStatusSummary().catch(() => []),
      CredentialingProvidersService.dashboardSpecialtiesAndTests().catch(() => ({ specialties: [], tests: [] })),
      CredentialingProvidersService.dashboardRankingByCity().catch(() => []),
    ]).then(([s, kp, c]) => {
      setStatuses(s); setSt(kp); setCities(c)
    }).finally(() => setLoading(false))
  }, [])

  const total = statuses.reduce((sum, s) => sum + s.count, 0)
  const aceitos    = statuses.find(s => s.status === 'ACEITO')?.count ?? 0
  const emProcesso = statuses.find(s => s.status === 'EM ANDAMENTO')?.count ?? 0
  const rejeitados = statuses.find(s => s.status === 'REJEITADO')?.count ?? 0

  // Exporta os rankings exatamente como exibidos (top 10 / top 10 / top 25), uma aba cada.
  function exportExcel() {
    const specialties = st.specialties.slice(0, 10).map((s, i) => ({ position: i + 1, name: s.name, count: s.providers }))
    const tests = st.tests.slice(0, 10).map((t, i) => ({ position: i + 1, name: t.name, count: t.providers }))
    const cityRows = cities.slice(0, 25).map((c, i) => ({ position: i + 1, name: c.cityName, state: c.state ?? '', count: c.count }))
    if (!specialties.length && !tests.length && !cityRows.length) {
      toast.error('Nenhum registro para exportar.')
      return
    }
    exportSheetsToXlsx<RankingExportRow>('ranking-credenciamento', [
      { name: 'Top especialidades', rows: specialties, columns: NAME_RANK_COLUMNS('Especialidade') },
      { name: 'Top exames',         rows: tests,       columns: NAME_RANK_COLUMNS('Exame') },
      { name: 'Ranking por cidade', rows: cityRows,    columns: CITY_RANK_COLUMNS },
    ])
  }

  return (
    <>
      <PageHeader title="Dashboard de credenciamento" subtitle="Visão da rede e ranking por especialidades/exames/cidades"
        crumbs={[{ label: 'Credenciamento' }, { label: 'Dashboard' }]}
        actions={
          <Button variant="secondary" size="sm" onClick={exportExcel} disabled={loading} iconLeft={<FileSpreadsheet size={14} />}>
            Exportar Excel
          </Button>
        } />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Total"            value={total}       icon={<Activity size={18} />}    accent="brand" />
        <KpiCard label="Ativos (ACEITO)"  value={aceitos}     icon={<Stethoscope size={18} />} accent="emerald" />
        <KpiCard label="Em processo"      value={emProcesso}  icon={<FlaskConical size={18} />} accent="amber" />
        <KpiCard label="Rejeitados"       value={rejeitados}  icon={<MapPin size={18} />}      accent="rose" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Card>
          <CardHeader title="Top especialidades" subtitle="Por nº de credenciados que oferecem" />
          <CardBody>
            <ul className="divide-y divide-gray-100">
              {st.specialties.length === 0 && <li className="text-sm text-gray-400 py-3">Sem dados.</li>}
              {st.specialties.slice(0, 10).map(s => (
                <li key={s.id} className="flex items-center justify-between py-2 text-sm">
                  <span className="text-gray-700">{s.name}</span>
                  <Badge variant="blue">{s.providers}</Badge>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Top exames" subtitle="Por nº de credenciados que oferecem" />
          <CardBody>
            <ul className="divide-y divide-gray-100">
              {st.tests.length === 0 && <li className="text-sm text-gray-400 py-3">Sem dados.</li>}
              {st.tests.slice(0, 10).map(t => (
                <li key={t.id} className="flex items-center justify-between py-2 text-sm">
                  <span className="text-gray-700">{t.name}</span>
                  <Badge variant="blue">{t.providers}</Badge>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader title="Ranking por cidade" subtitle="Top 25 cidades com credenciados ativos" />
        <CardBody>
          {loading
            ? <div className="text-sm text-gray-400 py-6 text-center">Carregando</div>
            : (
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500">Cidade</th>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500">UF</th>
                    <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-gray-500">Credenciados</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {cities.length === 0 && <tr><td colSpan={3} className="px-3 py-6 text-center text-gray-400">Sem dados.</td></tr>}
                  {cities.slice(0, 25).map(c => (
                    <tr key={c.cityId}>
                      <td className="px-3 py-2 text-gray-700">{c.cityName}</td>
                      <td className="px-3 py-2"><StatusBadge status={c.state ?? '—'} /></td>
                      <td className="px-3 py-2 text-right">{c.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </CardBody>
      </Card>
    </>
  )
}
